import { Component, inject, OnInit, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, DataTableComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, Language, PermissionAction } from "../../../shared/constants/enums";
import { CustomButton, TableButtonClicked } from "../../../shared/models";
import { AppService, PermissionsService, SystemLayoutService, UserGroupsApiService } from "../../../shared/services";

@Component({
  selector: "adaa-usergroups",
  imports: [DataTableComponent, TranslatePipe],
  templateUrl: "./user-groups.html",
  styleUrl: "./user-groups.scss",
})
export class UserGroupsComponent implements OnInit {
  private _router = inject(Router);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);
  private _userGroupsApiService = inject(UserGroupsApiService);
  private _permissionsService = inject(PermissionsService);
  private _systemLayoutService = inject(SystemLayoutService);

  Constants = Constants;
  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");
  private _untilDestroyed = AdaaHelper.untilDestroyed();

  customButtons: CustomButton[] = [];

  public ngOnInit(): void {
    this._permissionsService.isPermissionsLoaded.pipe(this._untilDestroyed()).subscribe((loaded: boolean) => {
      if (!loaded) return;

      this._checkPermissions();
    });

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this._appService.reloadPage("/console/users-groups"),
    });
  }

  private _checkPermissions() {
    const permission = this._permissionsService.hasPermission([
      {
        permissionAction: PermissionAction.manage,
        objectTypeId: Constants.CONSTANT_PERMISSIONS.USER_GROUPS,
      },
    ]);

    this.customButtons.push(
      {
        iconName: "adaa-icon-adaa-ok-Y",
        eventName: "enable",
        iconTitle: "common.form.label.active",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.Y === data?.enabled && permission;
        },
      },
      {
        iconName: "adaa-icon-adaa-ok-N",
        eventName: "disable",
        iconTitle: "common.form.label.inactive",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.N === data?.enabled && permission;
        },
      }
    );
  }

  public addNewClicked() {
    this._router.navigateByUrl("/console/users-groups/create");
  }

  private _deleteGroup(groupId: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.header = "user_groups.modals.delete_yes_no.title";
    modal.componentInstance.title = "user_groups.modals.delete_yes_no.info";

    modal.result.then((e) => {
      if (e) {
        this._userGroupsApiService.deleteGroup(groupId).subscribe({
          next: () => {
            this._toastrService.success(this._translateService.instant("notification.success.remove"));
            this.adaaDataTable().loadTableData();
          },
        });
      }
    });
  }

  private _updateState(value: TableButtonClicked) {
    const data = { id: value.data.id, enabled: value.event === "enable" ? "N" : "Y" };
    this._userGroupsApiService.updateState(data).subscribe({
      next: () => {
        this._toastrService.success(
          this._translateService.instant(
            "notification.success." + (value.event === "enable" ? "deactivate" : "activate")
          )
        );
        this.adaaDataTable().loadTableData();
      },
    });
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "edit":
        this._router.navigateByUrl(`/console/users-groups/edit/${value.data.id}`);
        break;
      case "view":
        this._router.navigateByUrl(`/console/users-groups/${value.data.id}`);
        break;
      case "delete":
        this._deleteGroup(value.data.id);
        break;
      case "enable":
      case "disable":
        this._updateState(value);
        break;

      default:
        break;
    }
  }
}
